import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useDarkMode } from '../../contexts/DarkModeContext'
import { getCookieConsent, setCookieConsent } from '../../utils/cookieConsent'
import { initializeAnalytics, reinitializeAnalytics } from '../../utils/analytics'
import './CookieConsent.scss'

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const { isDarkMode } = useDarkMode()

  useEffect(() => {
    // Check if user has already given consent
    const consent = getCookieConsent()
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setShowBanner(true)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (consent.accepted) {
      // If consent was already given, initialize analytics
      initializeAnalytics()
    }
  }, [])

  const handleAccept = () => {
    setCookieConsent(true)
    setShowBanner(false)
    // Initialize analytics when user accepts cookies
    initializeAnalytics()
  }

  const handleReject = () => {
    setCookieConsent(false)
    setShowBanner(false)
    // Reinitialize to stop tracking if consent is revoked
    reinitializeAnalytics()
  }

  const handleCustomize = () => {
    setShowDetails(true)
  }

  if (!showBanner) {
    return null
  }

  return (
    <div className={`cookie-consent-banner ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="cookie-consent-container">
        {!showDetails ? (
          <>
            <div className="cookie-consent-content">
              <div className="cookie-consent-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="cookie-consent-text">
                <h5 className="cookie-consent-title">We use cookies</h5>
                <p className="cookie-consent-description">
                  We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                  By clicking "Accept All", you consent to our use of cookies. You can also customize your preferences or 
                  view our <Link to="/privacy" className="cookie-consent-link">Privacy Policy</Link> for more information.
                </p>
              </div>
            </div>
            <div className="cookie-consent-actions">
              <button 
                className="btn btn-outline-secondary cookie-consent-btn cookie-consent-btn-reject"
                onClick={handleReject}
              >
                Reject All
              </button>
              <button 
                className="btn btn-outline-primary cookie-consent-btn cookie-consent-btn-customize"
                onClick={handleCustomize}
              >
                Customize
              </button>
              <button 
                className="btn btn-primary cookie-consent-btn cookie-consent-btn-accept"
                onClick={handleAccept}
              >
                Accept All
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="cookie-consent-content">
              <div className="cookie-consent-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="cookie-consent-text">
                <h5 className="cookie-consent-title">Cookie Preferences</h5>
                <p className="cookie-consent-description mb-3">
                  Manage your cookie preferences. You can enable or disable different types of cookies below.
                </p>
                
                <div className="cookie-consent-categories">
                  <div className="cookie-category-item">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <strong>Essential Cookies</strong>
                        <p className="mb-0 small text-muted">
                          Required for the website to function properly. These cannot be disabled.
                        </p>
                      </div>
                      <div className="form-check form-switch">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id="essential-cookies" 
                          checked 
                          disabled
                        />
                      </div>
                    </div>
                  </div>

                  <div className="cookie-category-item">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <strong>Analytics Cookies</strong>
                        <p className="mb-0 small text-muted">
                          Help us understand how visitors interact with our website.
                        </p>
                      </div>
                      <div className="form-check form-switch">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id="analytics-cookies" 
                          defaultChecked
                        />
                      </div>
                    </div>
                  </div>

                  <div className="cookie-category-item">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <strong>Marketing Cookies</strong>
                        <p className="mb-0 small text-muted">
                          Used to deliver personalized advertisements and track campaign performance.
                        </p>
                      </div>
                      <div className="form-check form-switch">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id="marketing-cookies" 
                          defaultChecked
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="cookie-consent-actions">
              <button 
                className="btn btn-outline-secondary cookie-consent-btn"
                onClick={() => setShowDetails(false)}
              >
                Back
              </button>
              <button 
                className="btn btn-primary cookie-consent-btn cookie-consent-btn-accept"
                onClick={handleAccept}
              >
                Save Preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default CookieConsent
